<template>
  <div>
    <BCard v-if="error" variant="danger" class="mb-3">
      {{ error }}
    </BCard>

    <form id="payment-form" class="mt-4">
      <div class="border-b mb-6 pb-6">
        <div class="sm:flex items-center sm:space-x-3 rtl:space-x-reverse mb-3">
          <UFormGroup :label="$t('general.firstName')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.givenName" type="text" :placeholder="$t('general.firstName')" flat />
          </UFormGroup>

          <UFormGroup :label="$t('general.lastName')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.familyName" type="text" :placeholder="$t('general.lastName')" flat />
          </UFormGroup>
        </div>

        <UFormGroup :label="$t('general.address')" class="w-full  mb-3">
          <UInput v-model="billingContact.addressLines[0]" type="text" :placeholder="$t('general.address')" flat />
        </UFormGroup>

        <div class="sm:flex items-center sm:space-x-3  rtl:space-x-reverse mb-3">
          <UFormGroup :label="$t('general.city')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.city" type="text" :placeholder="$t('general.city')" flat />
          </UFormGroup>
          <UFormGroup :label="$t('general.state')" class="w-full sm:w-1/2">
            <UInput v-model="billingContact.region" type="text" :placeholder="$t('general.state')" flat />
          </UFormGroup>
        </div>
      </div>

      <div v-if="integration.data.payment_option_afterpay" id="afterpay-button" class="w-full mb-2" @click="process('afterpay', false)" />

      <div v-if="integration.data.payment_option_google_pay" id="google-pay-button" class="w-full mb-2" @click="process('googlePay', true)" />

      <div v-if="integration.data.payment_option_apple_pay" id="apple-pay-button" class="w-full mb-2" @click="process('applePay', true)" />

      <!--<div v-if="integration.data.payment_option_card" id="card-container" class="mt-4" :class="{ 'hidden' : !card }"  />-->

      <div id="card-container" class="mt-4" />
    </form>

    <div id="payment-status-container" />

    <UButton block size="lg" :loading="processing" :disabled="disabled || processing" @click="process('card')">
      {{ confirmButtonTitle }}
    </UButton>

    <div v-if="loading" class="text-center">
      <BSpinner label="Loading" class="bookapp-action-tint-color" />
    </div>
  </div>
</template>

<script setup lang="ts">
import Bowser from 'bowser'

// Add Square types
declare global {
  interface Window {
    Square: {
      payments: (appId: string, locationId: string, options?: any) => any
    }
  }
}

interface Props {
  label: string
  amount: number
  customer: any
  confirmButtonTitle: string
  integration: any
  disabled: boolean
  action: Function
  intent?: string
}

const props = withDefaults(defineProps<Props>(), {
  intent: 'charge'
})

const emit = defineEmits(['callback'])

// Payment method instances
let card: any
let googlePay: any
let applePay: any
let afterpay: any

const payments = ref()
const loading = ref(true)
const processing = ref(false)
const billingContact = ref({
  familyName: '',
  givenName: '',
  email: '',
  addressLines: [''],
  city: '',
  region: '',
  countryCode: ''
})

const error = ref()

// Load Square Web Payment SDK script
const load = () => {
  if (window.Square) {
    nextTick(() => {
      initialize()
    })
  } else {
    const data = props.integration.data
    const sandbox = data.sandbox || false

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = `https://${sandbox ? 'sandbox.' : ''}web.squarecdn.com/v1/square.js`
    script.async = true
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        initialize()
      })
    }
  }
}

// Initialize Square Web Payments SDK
const initialize = async () => {
  const appId = props.integration.data.applicationID
  const locationId = props.integration.data.locationID

  try {
    // Initialize Web Payments SDK
    payments.value = window.Square.payments(appId, locationId, {
      scriptSrc: `https://${props.integration.data.sandbox ? 'sandbox.' : ''}web.squarecdn.com/v1/square.js`
    })

    // Initialize payment methods
    await Promise.all([
      props.integration.data.payment_option_card && initializeCard(),
      props.integration.data.payment_option_google_pay && initializeGooglePay(),
      props.integration.data.payment_option_apple_pay && initializeApplePay(),
      props.integration.data.payment_option_afterpay && initializeAfterPay()
    ])

    loading.value = false
  } catch (e) {
    console.error('Failed to initialize Square:', e)
    error.value = 'Something went wrong while loading Square.'
  }
}

// Build payment request configuration
const buildPaymentRequest = (payments: any) => {
  const amount = Number(props.amount || 0).toFixed(2).toString()
  const currencyCode = props.integration.data.currency
  const countryCode = props.integration.data.country

  return payments.paymentRequest({
    requestShippingContact: false,
    countryCode,
    currencyCode,
    total: {
      amount,
      label: 'Total',
    },
  })
}

// Initialize Card payment method
const initializeCard = async () => {
  try {
    card = await payments.value.card({})

    await card.attach('#card-container')
  } catch (e) {
    console.error('Failed to initialize card:', e)
    error.value = 'Failed to initialize card payment method.'
  }
}

// Initialize Google Pay
const initializeGooglePay = async () => {
  const browser = Bowser.getParser(window.navigator.userAgent)

  if (browser.isBrowser('Chrome')) {
    try {
      const paymentRequest = buildPaymentRequest(payments.value)
      googlePay = await payments.value.googlePay(paymentRequest)
      await googlePay.attach('#google-pay-button', {
        buttonSizeMode: 'fill',
        buttonColor: 'black',
        buttonType: 'plain',
      })
    } catch (e) {
      console.error('Failed to initialize Google Pay:', e)
    }
  }
}

// Initialize Apple Pay
const initializeApplePay = async () => {
  try {
    const paymentRequest = buildPaymentRequest(payments.value)
    applePay = await payments.value.applePay(paymentRequest)
    await applePay.attach('#apple-pay-button')
  } catch (e) {
    console.error('Failed to initialize Apple Pay:', e)
  }
}

// Initialize AfterPay / ClearPay
const initializeAfterPay = async () => {
  if (props.intent === 'store') {
    return
  }

  try {
    const paymentRequest = buildPaymentRequest(payments.value)
    afterpay = await payments.value.afterpayClearpay(paymentRequest)
    await afterpay.attach('#afterpay-button')
  } catch (e) {
    console.error('Failed to initialize AfterPay:', e)
  }
}

// Process payment
const process = async (paymentMethod: string) => {
  processing.value = true
  error.value = null

  try {
    let method = card

    if (paymentMethod === 'googlePay') {
      method = googlePay
    } else if (paymentMethod === 'applePay') {
      method = applePay
    } else if (paymentMethod === 'afterpay') {
      method = afterpay
    }

    const verificationDetails: {
      amount?: string
      currencyCode?: string
      billingContact: any
      intent: string
      customerInitiated: boolean
      sellerKeyedIn: boolean
    }  = {
      billingContact: JSON.parse(JSON.stringify(billingContact.value)),
      intent: props.intent === 'store' ? 'STORE' : 'CHARGE',
      customerInitiated: true,
      sellerKeyedIn: false
    }

    // Only add amount and currencyCode for CHARGE intent
    if (props.intent !== 'store') {
      verificationDetails.amount = props.amount.toString()
      verificationDetails.currencyCode = props.integration.data.currency
    }

    const tokenResult = await method.tokenize(verificationDetails)

    if (tokenResult.status === 'OK') {
      emit('callback', { 
        success: true, 
        params: { 
          squareToken: tokenResult.token 
        } 
      })
    } else {
      let errorMessage = `Tokenization failed with status: ${tokenResult.status}`
      if (tokenResult.errors) {
        errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`
      }
      throw new Error(errorMessage)
    }
  } catch (e) {
    console.error('Payment processing error:', e)
    error.value = e instanceof Error ? e.message : 'Payment processing failed'
  } finally {
    processing.value = false
  }
}

onMounted(() => {
  if (props.customer) {
    if (props.customer.address) {
      billingContact.value.addressLines[0] = props.customer.address
    }
    if (props.customer.address2) {
      billingContact.value.addressLines[1] = props.customer.address2
    }

    if (props.customer.suburb && props.customer.city) {
      billingContact.value.city = props.customer.suburb + ', ' + props.customer.city
    } else if (props.customer.suburb) {
      billingContact.value.city = props.customer.suburb
    } else if (props.customer.city) {
      billingContact.value.city = props.customer.city
    }

    if (props.customer.region) {
      billingContact.value.region = props.customer.region
    }

    if (props.customer.firstName) {
      billingContact.value.givenName = props.customer.firstName
    }

    if (props.customer.lastName) {
      billingContact.value.familyName = props.customer.lastName
    }

    if (props.customer.email) {
      billingContact.value.email = props.customer.email
    }

    if (props.customer.country) {
      billingContact.value.countryCode = props.customer.country
    }
  }

  load()
})
</script>

<style>
#apple-pay-button {
  height: 40px;
  width: 100%;
  -webkit-appearance: -apple-pay-button;
  -apple-pay-button-type: plain;
  -apple-pay-button-style: black;
}

#google-pay-button button.gpay-button.black.book.new_style.en {
  border-radius: 6px;
  border: none;
  padding: 9px 15%;
  min-height: 40px;
}

#card-container {
  min-height: 100px;
}
</style>

